/**
 * Обзор раздела «Экология» клиента-компании (`/ecology`, ecology-sites, макет варианта Б 17.09.2026):
 * плитки подразделов «Проектные работы» и «Отчётность» со сводкой по цветам светофора, площадки
 * организации (добавить / изменить / удалить, опрос по площадке) и пояснение цветов. Ежегодный вопрос
 * «что менялось» — плашкой, когда по площадке пора спросить, и кнопкой «Были изменения» в любой
 * момент (ecology-survey). Третья плитка — «Абонентское обслуживание» (ecology-subscription):
 * у абонента коротко о сопровождении, у остальных — приглашение к тарифам.
 */
import {
  ChevronRight,
  ClipboardList,
  FileCheck2,
  FileClock,
  FlaskConical,
  Handshake,
  ShoppingCart,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { EcoSection, EcoStatusCounts, EcoSubscription } from '@/lib/api'
import { ecologyClientApi } from '@/lib/api'
import { ecoSummaryLines, formatEcoDate } from '@/lib/ecology'
import { AppLink } from '@/app/navigation'
import { useEcoData } from '@/ecology/use-eco-data'
import { useStartEcoOrder } from '@/ecology/order/start-order'
import { SitesPanel } from '@/ecology/sites-panel'
import { EcoStatusLegend } from '@/ecology/status-legend'
import {
  ChangeCheckBanner,
  ecoChangeCheckDueSites,
  useEcoChangeCheck,
} from '@/ecology/change-check'
import { Button } from '@/design-system/components/primitives/button'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { StatusDot } from '@/design-system/components/primitives/status-dot'
import { Alert } from '@/design-system/components/feedback/alert'

const TILES: {
  section: EcoSection
  title: string
  to: string
  hint: string
  empty: string
  icon: typeof FileCheck2
}[] = [
  {
    section: 'projects',
    title: 'Проектные работы',
    to: '/ecology/projects',
    hint: 'НДВ, НМУ, ПЭК, СЗЗ, ПНООЛР, паспорта отходов',
    empty: 'Документов пока нет — внесите имеющиеся',
    icon: FileCheck2,
  },
  {
    section: 'reports',
    title: 'Отчётность',
    to: '/ecology/reports',
    hint: '2-ТП, декларация о плате, отчёт ПЭК, журнал отходов',
    empty: 'Отчётности пока нет — отметьте сданные отчёты',
    icon: FileClock,
  },
  {
    section: 'lab',
    title: 'Лабораторный контроль',
    to: '/ecology/lab',
    hint: 'Замеры на границе СЗЗ, на источниках выбросов, воды',
    empty: 'Контроля пока нет — отметьте выполненные замеры',
    icon: FlaskConical,
  },
]

function SummaryTile({ tile, counts }: { tile: (typeof TILES)[number]; counts: EcoStatusCounts }) {
  const lines = ecoSummaryLines(tile.section, counts)
  const Icon = tile.icon
  return (
    <AppLink
      to={tile.to}
      className="ds-surface group flex flex-col rounded-12 px-4 py-3.5 text-txt hover:bg-hover hover:text-txt hover:no-underline"
    >
      <span className="flex items-center gap-2 text-15 font-semibold text-txt">
        <Icon className="size-4 shrink-0 stroke-current text-txt-secondary" aria-hidden />
        {tile.title}
        <ChevronRight
          className="ml-auto size-4 shrink-0 stroke-current text-txt-tertiary group-hover:text-txt-secondary"
          aria-hidden
        />
      </span>
      <span className="mt-2 flex flex-col gap-1 text-13">
        {lines.length === 0 ? (
          <span className="text-txt-secondary">{tile.empty}</span>
        ) : (
          lines.map((l) => (
            <span key={l.color} className="flex items-center gap-2 text-txt">
              <StatusDot variant={l.color} label="" aria-hidden />
              {l.text}
            </span>
          ))
        )}
      </span>
      <span className="mt-2 text-12 text-txt-tertiary">{tile.hint}</span>
    </AppLink>
  )
}

/** Плитка «Абонентское обслуживание»: сопровождение или приглашение к тарифам. */
function SubscriptionTile({ subscription }: { subscription: EcoSubscription | null }) {
  const lines = subscription
    ? [
        {
          color: 'green' as const,
          text: subscription.contractUntil
            ? `тариф «${subscription.tariffLabel}» до ${formatEcoDate(subscription.contractUntil)}`
            : `тариф «${subscription.tariffLabel}»`,
        },
        ...(subscription.nextVisitOn
          ? [
              {
                color: 'gray' as const,
                text: `выезд инженера ${formatEcoDate(subscription.nextVisitOn)}`,
              },
            ]
          : []),
        ...(subscription.planTotal > 0
          ? [
              {
                color:
                  subscription.planDone === subscription.planTotal
                    ? ('green' as const)
                    : ('amber' as const),
                text: `план работ: ${subscription.planDone} из ${subscription.planTotal}`,
              },
            ]
          : []),
      ]
    : []
  return (
    <AppLink
      to="/ecology/subscription"
      className="ds-surface group flex flex-col rounded-12 px-4 py-3.5 text-txt hover:bg-hover hover:text-txt hover:no-underline"
    >
      <span className="flex items-center gap-2 text-15 font-semibold text-txt">
        <Handshake className="size-4 shrink-0 stroke-current text-txt-secondary" aria-hidden />
        Абонентское обслуживание
        <ChevronRight
          className="ml-auto size-4 shrink-0 stroke-current text-txt-tertiary group-hover:text-txt-secondary"
          aria-hidden
        />
      </span>
      <span className="mt-2 flex flex-col gap-1 text-13">
        {lines.length === 0 ? (
          <span className="text-txt-secondary">
            Сопровождения нет — посмотрите тарифы «Защита» и «Прайм»
          </span>
        ) : (
          lines.map((l) => (
            <span key={l.text} className="flex items-center gap-2 text-txt">
              <StatusDot variant={l.color} label="" aria-hidden />
              {l.text}
            </span>
          ))
        )}
      </span>
      <span className="mt-2 text-12 text-txt-tertiary">
        {subscription ? 'ведёт инженер Центрики' : 'экологию ведёт инженер Центрики'}
      </span>
    </AppLink>
  )
}

export function EcologyOverviewPage() {
  const navigate = useNavigate()
  const { overview, settings, loading, error, setOverview } = useEcoData(ecologyClientApi)
  const changeCheck = useEcoChangeCheck(ecologyClientApi, overview, setOverview)
  const order = useStartEcoOrder()

  return (
    <div>
      <PageHead
        title="Экология"
        subtitle="Площадки, категории НВОС и сводка по направлениям"
        actions={
          overview ? (
            <>
              <Button variant="secondary" onClick={changeCheck.openChanges}>
                Были изменения
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate('/ecology/survey')}
                leftIcon={<ClipboardList className="size-4 stroke-current" aria-hidden />}
              >
                Пройти опрос
              </Button>
              <Button
                disabled={order.starting}
                onClick={() => order.start({})}
                leftIcon={<ShoppingCart className="size-4 stroke-current" aria-hidden />}
              >
                Заказать услугу
              </Button>
            </>
          ) : null
        }
      />
      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading || !overview ? (
        <Panel>
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        </Panel>
      ) : (
        <>
          {order.error ? (
            <Alert variant="danger" className="mb-4">
              {order.error}
            </Alert>
          ) : null}
          <ChangeCheckBanner
            sites={ecoChangeCheckDueSites(overview)}
            busy={changeCheck.busy}
            onNothingChanged={changeCheck.nothingChanged}
            onChanges={changeCheck.openChanges}
          />
          {changeCheck.message ? <div className="mb-4">{changeCheck.message}</div> : null}
          {/* Четыре плитки — сеткой 2×2: в одну строку на экране кабинета им тесно. */}
          <div className="mb-5 grid gap-3 nw:grid-cols-2">
            <SummaryTile tile={TILES[0]} counts={overview.documents} />
            <SummaryTile tile={TILES[1]} counts={overview.reports} />
            <SummaryTile tile={TILES[2]} counts={overview.lab} />
            <SubscriptionTile subscription={overview.subscription} />
          </div>
          <SitesPanel
            api={ecologyClientApi}
            overview={overview}
            onChange={setOverview}
            onSurvey={(site) => navigate(`/ecology/survey?site=${site.id}`)}
          />
          <EcoStatusLegend settings={settings} />
          {changeCheck.dialog}
        </>
      )}
    </div>
  )
}
